import React, { createContext, useContext, useMemo } from 'react';

import { useRelayEnvironment } from 'react-relay';
import type { IEnvironment } from 'relay-runtime';

import { getAgentSessionsPolicy } from './agentSessionsPolicy';
import type { AgentSessionsScope } from './types';
import { useRovoActivationId } from './useRovoActivationId';

export type ResolvedAgentSessionsScope = Readonly<{
	accountId: string;
	cloudId?: string;
	rovoActivationId: string;
	workspaceAri: string;
	enableExplicitAgentSessionSubscriptionSharding?: boolean;
}>;

type ScopeContext = Readonly<{
	environment: IEnvironment;
	input: AgentSessionsScope;
	scope: ResolvedAgentSessionsScope | null;
	reason?: 'missing-identity' | 'no-active-rovo-activation';
}>;

const Context = createContext<ScopeContext | null>(null);

export const AgentSessionsScopeProvider = ({
	scope: input,
	children,
}: {
	scope: AgentSessionsScope;
	children: React.ReactNode;
}): React.JSX.Element => {
	const environment = useRelayEnvironment();
	const resolution = useRovoActivationId(input);
	const activationId = resolution.status === 'ready' ? resolution.rovoActivationId : undefined;
	const reason = resolution.status === 'unavailable' ? resolution.reason : undefined;
	const accountId = input.accountId.trim();
	const cloudId = input.cloudId?.trim();
	const enableExplicitAgentSessionSubscriptionSharding =
		getAgentSessionsPolicy().sharedResources &&
		input.enableExplicitAgentSessionSubscriptionSharding === true;
	const value = useMemo<ScopeContext>(
		() => ({
			environment,
			input: cloudId ? { accountId, cloudId } : { accountId, rovoActivationId: activationId ?? '' },
			scope: activationId
				? {
						accountId,
						cloudId,
						rovoActivationId: activationId,
						workspaceAri: `ari:cloud:rovo::workspace/${activationId}`,
						...(enableExplicitAgentSessionSubscriptionSharding
							? { enableExplicitAgentSessionSubscriptionSharding }
							: {}),
					}
				: null,
			reason,
		}),
		[
			accountId,
			activationId,
			enableExplicitAgentSessionSubscriptionSharding,
			cloudId,
			environment,
			reason,
		],
	);
	return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useAgentSessionsScope = (): ScopeContext => {
	const context = useContext(Context);
	if (!context) throw new Error('AgentSessionsScopeProvider is required.');
	return context;
};

export const getAgentSessionsScopeKey = (scope: ResolvedAgentSessionsScope): string =>
	JSON.stringify([scope.accountId, scope.workspaceAri]);
